import { useEffect, useState, memo } from 'react'
import { Palette, Sun, Moon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const THEME_DATA = [
  {
    titleKey: 'themes.preferredThemes',
    themes: [
      { name: 'light', label: 'Light', color: '#570DF8', type: 'light' },
      { name: 'dark', label: 'Dark', color: '#661AE6', type: 'dark' },
      { name: 'corporate', label: 'Corporate', color: '#4B6BFB', type: 'light' },
      { name: 'business', label: 'Business', color: '#1C4E80', type: 'dark' },
      { name: 'dim', label: 'Dim', color: '#9FE88D', type: 'dark' },
    ]
  },
  {
    titleKey: 'themes.lightThemes',
    themes: [
      { name: 'cupcake', label: 'Cupcake', color: '#65C3C8', type: 'light' },
      { name: 'bumblebee', label: 'Bumblebee', color: '#F9D72F', type: 'light' },
      { name: 'emerald', label: 'Emerald', color: '#66CC8A', type: 'light' },
      { name: 'retro', label: 'Retro', color: '#EF9995', type: 'light' },
      { name: 'valentine', label: 'Valentine', color: '#E96D7B', type: 'light' },
      { name: 'garden', label: 'Garden', color: '#5C7F67', type: 'light' },
      { name: 'lofi', label: 'Lo-Fi', color: '#0D0D0D', type: 'light' },
      { name: 'pastel', label: 'Pastel', color: '#D1C1D7', type: 'light' },
      { name: 'fantasy', label: 'Fantasy', color: '#6E0B75', type: 'light' },
      { name: 'wireframe', label: 'Wireframe', color: '#B8B8B8', type: 'light' },
      { name: 'cmyk', label: 'CMYK', color: '#45AEEE', type: 'light' },
      { name: 'autumn', label: 'Autumn', color: '#8C0327', type: 'light' },
      { name: 'acid', label: 'Acid', color: '#FF00F4', type: 'light' },
      { name: 'lemonade', label: 'Lemonade', color: '#519903', type: 'light' },
      { name: 'winter', label: 'Winter', color: '#047AFF', type: 'light' },
      { name: 'nord', label: 'Nord', color: '#5E81AC', type: 'light' },
      { name: 'sunset', label: 'Sunset', color: '#FF6F3C', type: 'light' },
    ]
  },
  {
    titleKey: 'themes.darkThemes',
    themes: [
      { name: 'synthwave', label: 'Synthwave', color: '#E779C1', type: 'dark' },
      { name: 'halloween', label: 'Halloween', color: '#F28C18', type: 'dark' },
      { name: 'forest', label: 'Forest', color: '#1EB854', type: 'dark' },
      { name: 'aqua', label: 'Aqua', color: '#09ECEC', type: 'dark' },
      { name: 'black', label: 'Black', color: '#373737', type: 'dark' },
      { name: 'luxury', label: 'Luxury', color: '#FFFFFF', type: 'dark' },
      { name: 'dracula', label: 'Dracula', color: '#FF79C6', type: 'dark' },
      { name: 'night', label: 'Night', color: '#38BDF8', type: 'dark' },
      { name: 'coffee', label: 'Coffee', color: '#DB924B', type: 'dark' },
      { name: 'cyberpunk', label: 'Cyberpunk', color: '#FF7598', type: 'dark' },
      { name: 'abyss', label: 'Abyss', color: '#00FFFF', type: 'dark' },
    ]
  }
] as const

function ThemeSwitcher() {
  const { t } = useTranslation()

  const [currentTheme, setCurrentTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'light'
    }
    return 'light'
  })

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTheme)
    localStorage.setItem('theme', currentTheme)
  }, [currentTheme])

  const handleThemeChange = (themeName: string) => {
    setCurrentTheme(themeName)
    const elem = document.activeElement as HTMLElement
    elem?.blur()
  }

  return (
    <div className="dropdown dropdown-end">
      <div tabIndex={0} role="button" className="btn btn-ghost btn-circle" title={t('common.theme')}>
        <Palette className="w-5 h-5" />
      </div>
      <div tabIndex={-1} className="dropdown-content bg-base-100 rounded-box z-[1] mt-3 w-52 p-2 shadow max-h-[32rem] overflow-y-auto overflow-x-hidden">
        <ul className="menu menu-sm w-full min-w-0">
          {THEME_DATA.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              <li className="menu-title">
                <span className="text-xs font-semibold opacity-70">{t(category.titleKey)}</span>
              </li>

              {category.themes.map((theme) => (
                <li key={theme.name}>
                  <a
                    onClick={() => handleThemeChange(theme.name)}
                    className={currentTheme === theme.name ? 'active' : ''}
                  >
                    <div
                      className="w-4 h-4 rounded-full border border-base-300 flex-shrink-0"
                      style={{ backgroundColor: theme.color }}
                    ></div>
                    <span className="flex-1">{theme.label}</span>
                    {theme.type === 'light' ? (
                      <Sun className="w-3 h-3 opacity-50 flex-shrink-0" />
                    ) : (
                      <Moon className="w-3 h-3 opacity-50 flex-shrink-0" />
                    )}
                    {currentTheme === theme.name && (
                      <span className="text-success flex-shrink-0">✓</span>
                    )}
                  </a>
                </li>
              ))}
            </div>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default memo(ThemeSwitcher)
